import { LEVEL_LABELS } from "../consts";

/**
 * Managerial Report download: every object (Community / Zone / Block / Property section) with its
 * category, report group and GL rows, as Excel-readable CSV or as TSV. Columns follow the screen:
 * the Report Grp column only with By Sub-Groups, budget columns only With Budgets, unit totals only
 * with Report Units.
 */
const num = (a?: any) => (a?.amount === null || a?.amount === undefined ? "" : Number(a.amount).toFixed(2));
// Amounts are signed (CR negative); the DR/CR column carries the direction, so export the size (as on screen)
const size = (a?: any) => (a?.amount === null || a?.amount === undefined ? "" : Math.abs(Number(a.amount)).toFixed(2));
const drcr = (a?: any) => a?.drCr || "";

export const downloadManagerialReport = (result: any, criteria: any, format: "CSV" | "TSV", fileBase: string) => {
  const bySubGroups = criteria?.reportExpansion !== "BY_GL_ACCOUNTS";
  const withBudget = criteria?.budgets === "WITH_BUDGET";
  const withUnits = criteria?.unitDisplay !== "SUPPRESS_UNITS";

  const header = ["Level", "Object", "Units", "Account Category", ...(bySubGroups ? ["Report Group"] : []), "GL Account", "Description",
    "Reporting Period Actual", "DR/CR",
    ...(withBudget ? ["Reporting Period Budget", "DR/CR", "Variance", "Annual Budget"] : []),
    ...(withUnits ? ["Total Amount", "Total Units", "Amount / Unit"] : [])];
  const rows: any[][] = [header];

  const amounts = (r: any) => [size(r.reportingPeriodActual), drcr(r.reportingPeriodActual),
    ...(withBudget ? [size(r.reportingPeriodBudget), drcr(r.reportingPeriodBudget), num(r.variance), size(r.annualBudget)] : [])];

  const sections: [string, any[]][] = [
    ["COMMUNITY", result?.communityWise || []], ["ZONE", result?.zoneWise || []],
    ["BLOCK", result?.blockWise || []], ["PROPERTY", result?.propertyWise || []],
  ];
  for (const [level, nodes] of sections) {
    for (const node of nodes) {
      const base = [LEVEL_LABELS[level], node.objectName, node.units];
      for (const cat of node.categories || []) {
        rows.push([...base, cat.accountCategoryName, ...(bySubGroups ? [""] : []), "", "", ...amounts(cat),
          ...(withUnits ? [Math.abs(Number(cat.totalAmount ?? 0)).toFixed(2), cat.totalUnits ?? "", Math.abs(Number(cat.amountPerUnit ?? 0)).toFixed(2)] : [])]);
        for (const grp of cat.children || []) {
          if (bySubGroups && grp.reportingGroupName) {
            rows.push([...base, cat.accountCategoryName, grp.reportingGroupName, "", "", ...amounts(grp), ...(withUnits ? ["", "", ""] : [])]);
          }
          for (const gl of grp.children || []) {
            rows.push([...base, cat.accountCategoryName, ...(bySubGroups ? [grp.reportingGroupName || ""] : []), gl.glAccountNumber, gl.description,
              ...amounts(gl), ...(withUnits ? ["", "", ""] : [])]);
          }
        }
      }
    }
  }

  const sep = format === "TSV" ? "\t" : ",";
  const cell = (v: any) => {
    const s = v === null || v === undefined ? "" : String(v);
    if (format === "TSV") return s.replace(/[\t\r\n]+/g, " ");
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = rows.map((r) => r.map(cell).join(sep)).join("\r\n");
  // BOM so Excel opens the CSV as UTF-8
  const blob = new Blob(["﻿" + text], { type: format === "TSV" ? "text/tab-separated-values" : "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileBase}.${format === "TSV" ? "tsv" : "csv"}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
